import {
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import {
  AmpPlugin,
  easepick,
  PresetPlugin,
  RangePlugin
} from "@easepick/bundle";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { dateUtil } from "src/app/support/date-util";
import { compact, pickBy } from "lodash";
import { TranslocoService } from "@jsverse/transloco";

type PicketSelectEvent = {
  detail: {
    start: Date;
    end: Date;
  };
};

@Directive({
  selector: "[daterange]",
  standalone: false
})
export class DaterangePickerDirective implements OnInit, OnChanges, OnDestroy {
  private elementRef: ElementRef<HTMLInputElement> = inject(ElementRef);
  private pickerRef: easepick.Core | undefined;

  @Input("daterangeCustomPresets")
  customPresets: Record<string, [Date, Date]> | undefined;

  @Input()
  from: string | undefined;

  @Input()
  to: string | undefined;

  @Input()
  disableCustomPresets: boolean = false;

  @Input()
  disableResetButton: boolean = false;

  @Output()
  select: EventEmitter<[Date, Date]> = new EventEmitter<[Date, Date]>();

  @Output()
  clear: EventEmitter<any> = new EventEmitter<any>();

  private readonly translocoService = inject(TranslocoService);

  ngOnInit(): void {
    const config = <any>pickBy({
      element: this.elementRef.nativeElement,
      zIndex: 10,
      format: "DD/MM/YYYY",
      css: [
        "https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.0/dist/index.css"
      ],
      lang: this.translocoService.getActiveLang(),
      plugins: compact([
        RangePlugin,
        this.disableCustomPresets ? null : PresetPlugin,
        AmpPlugin
      ]),
      RangePlugin: {
        tooltip: true
      },
      PresetPlugin: this.disableCustomPresets
        ? undefined
        : {
            customPreset: this.customPresets || {
              [sntTranslate(marker("Mese precedente"))]: [
                dateUtil().add(-1, "month").startOf("month").toDate(),
                dateUtil().add(-1, "month").endOf("month").toDate()
              ],
              [sntTranslate(marker("2 mesi precedenti"))]: [
                dateUtil().add(-2, "month").startOf("month").toDate(),
                dateUtil().add(-1, "month").endOf("month").toDate()
              ],
              [sntTranslate(marker("3 mesi precedenti"))]: [
                dateUtil().add(-3, "month").startOf("month").toDate(),
                dateUtil().add(-1, "month").endOf("month").toDate()
              ],
              [sntTranslate(marker("6 mesi precedenti"))]: [
                dateUtil().add(-6, "month").startOf("month").toDate(),
                dateUtil().add(-1, "month").endOf("month").toDate()
              ]
            },
            customLabels: []
          },
      AmpPlugin: {
        resetButton: !this.disableResetButton
      }
    });

    this.pickerRef = new easepick.create(config);

    this.pickerRef.on("select", (pickerEvent: PicketSelectEvent) => {
      this.select.emit([pickerEvent.detail.start, pickerEvent.detail.end]);
      this.pickerRef?.hide();
    });

    this.pickerRef.on("clear", () => {
      this.clear.emit();
      this.pickerRef?.hide();
    });

    if (this.from && this.to) {
      this.pickerRef.setDateRange(
        dateUtil(this.from).toDate(),
        dateUtil(this.to).toDate()
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const changesFrom = changes["from"];
    const changesTo = changes["to"];

    if (changesFrom && !changesFrom.firstChange) {
      this.pickerRef?.setDateRange(
        dateUtil(this.from).toDate(),
        this.pickerRef.getEndDate()
      );
    }

    if (changesTo && !changesTo.firstChange) {
      this.pickerRef?.setDateRange(
        dateUtil(this.from).toDate(),
        this.pickerRef.getEndDate()
      );
    }

    if (
      (changesFrom && !changesFrom.firstChange && !changesFrom.currentValue) ||
      (changesTo && !changesTo.firstChange && !changesTo.currentValue)
    ) {
      this.pickerRef?.clear();
    }
  }

  ngOnDestroy(): void {
    this.pickerRef?.destroy();
  }
}
